import filter from "lodash/filter";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import {
  FIELD_MARKED_FOR_REMOVAL_KEY,
  FIELD_NAME_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { checkIsDirty } from "PFApp/helpers/activity_form/check_is_dirty";
import { useWatchIsNew } from "PFApp/hooks/activity_form/multi_roles_form/use_watch_is_new";
import { RoleFormKey } from "PFApp/types/activity_form/activity_form_schema";
import { Stack } from "PFComponents/containers";
import { DropdownButton } from "PFComponents/dropdown_button";
import { Pill, PillKind } from "PFComponents/pill";
import { Tooltip } from "PFComponents/tooltip";
import { Typography } from "PFComponents/typography";
import { IconName } from "PFTheme/graphics/icons";
import { useMemo } from "react";
import { useFormState, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import css from "./actions_cell.module.scss";
import { ActionType } from "./use_actions_column";

type StatusPillAttributes = {
  kind: PillKind;
  icon: IconName;
  tooltipText: string;
  ariaLabel: string;
};

type ActionsCellProps = {
  roleFormKey: RoleFormKey;
  onChange: (roleFormKey: RoleFormKey, action: ActionType) => void;
  actionsBlacklist?: ActionType[];
  disableIfRoleExists?: boolean;
};

export const ActionsCell = ({
  roleFormKey,
  onChange,
  actionsBlacklist,
  disableIfRoleExists
}: ActionsCellProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "engagementBlueprint" });
  // it's important to use here useFormState, useFormContext re-renders all on toggle dirty fields
  const { dirtyFields, errors } = useFormState();
  const isNew = useWatchIsNew(roleFormKey);

  const isMarkedForRemoval = useWatch({
    name: `${roleFormKey}.${FIELD_MARKED_FOR_REMOVAL_KEY}`
  });
  const roleName = useWatch({
    name: `${roleFormKey}.${FIELD_NAME_KEY}`
  });

  const hasErrors = !isEmpty(get(errors, roleFormKey));
  const isEdited = checkIsDirty(get(dirtyFields, roleFormKey), { simplified: true }) && !isNew;

  const statusPill = useMemo((): StatusPillAttributes | null => {
    if (isMarkedForRemoval) {
      const tooltipText = t("status.markedForRemoval");
      return {
        kind: "warning",
        icon: "remove",
        tooltipText,
        ariaLabel: `${roleName}: ${tooltipText}`
      };
    }
    if (hasErrors) {
      const tooltipText = t("status.failedToSave");
      return {
        kind: "danger",
        icon: "error",
        tooltipText,
        ariaLabel: `${roleName}: ${tooltipText}`
      };
    }
    if (isEdited || isNew) {
      const tooltipText = t("status.notSavedYet");
      return {
        kind: "general",
        icon: "edit",
        tooltipText,
        ariaLabel: `${roleName}: ${tooltipText}`
      };
    }
    if (!isNew && !isEdited && !hasErrors) {
      const tooltipText = t("status.saved");
      return {
        kind: "success",
        icon: "check",
        tooltipText,
        ariaLabel: `${roleName}: ${tooltipText}`
      };
    }

    return null;
  }, [isMarkedForRemoval, hasErrors, isEdited, isNew, t, roleName]);

  const options = useMemo(
    () =>
      filter(
        isMarkedForRemoval
          ? [
              {
                id: ActionType.UndoMarkForRemoval,
                displayElement: t("actions.undoMarkForRemoval"),
                item: ActionType.UndoMarkForRemoval
              }
            ]
          : [
              // TODO: [SP-7393] Disable Bulk add Hours action when role has id (is already created)
              {
                id: ActionType.BulkAddHours,
                displayElement: t("actions.bulkAddHours"),
                item: ActionType.BulkAddHours
              },
              {
                id: ActionType.Duplicate,
                displayElement: t("actions.duplicate"),
                item: ActionType.Duplicate
              },
              {
                id: ActionType.EditAvailability,
                displayElement: t("actions.editAvailability"),
                item: ActionType.EditAvailability
              },
              {
                id: isNew ? ActionType.Remove : ActionType.MarkForRemoval,
                displayElement: (
                  <Typography variant="bodyRegular" className={css.highlighted}>
                    {isNew ? t("actions.remove") : t("actions.markForRemoval")}
                  </Typography>
                ),
                item: isNew ? ActionType.Remove : ActionType.MarkForRemoval
              }
            ],
        ({ id }) => !actionsBlacklist?.includes(id as ActionType)
      ),
    [actionsBlacklist, t, isNew, isMarkedForRemoval]
  );

  return (
    <Stack alignItems="center" justifyContent="start" className={css.actionCellContent} gap="spacingMd">
      <DropdownButton
        actionIcon="menu-vertical"
        small
        options={options}
        disabled={disableIfRoleExists && !isNew}
        handleChange={(item) => onChange(roleFormKey, item)}
        dropDownStyle={{ overflow: "visible" }}
        aria-label={t("actions.moreOptionsForRole", { roleName: roleName || t("emptyRoleName") })}
        floatingOptions={{ strategy: "fixed" }}
      />
      {statusPill && (
        <Tooltip content={statusPill.tooltipText}>
          <Pill kind={statusPill.kind} icon={statusPill.icon} small aria-label={statusPill.ariaLabel} />
        </Tooltip>
      )}
    </Stack>
  );
};
